/**
 * Comercial → Itens a vencer (#MT-D552104).
 * Prévia + CSV. Janela = N meses civis seguintes, sem o mês atual.
 */
import api from '../api.js';
import msgbox, { notify } from '../../versa/msgbox.js';
import progress from '../../versa/progress.js';

function $(sel, root = document) {
	return root.querySelector(sel);
}

function fmtQty(v) {
	const n = Number(v);
	if (!Number.isFinite(n)) return v == null ? '' : String(v);
	return n.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
}

function fmtMoney(v, digits = 4) {
	const n = Number(v);
	if (!Number.isFinite(n)) return v == null ? '' : String(v);
	return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: digits });
}

function fmtIsoBr(iso) {
	if (!iso) return '';
	const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (!m) return String(iso);
	return `${m[3]}/${m[2]}/${m[1]}`;
}

function downloadBase64(b64, filename, mime) {
	const bin = atob(b64);
	const bytes = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
	const a = document.createElement('a');
	a.href = URL.createObjectURL(new Blob([bytes], { type: mime }));
	a.download = filename || 'download';
	a.click();
	URL.revokeObjectURL(a.href);
}

/**
 * Calcula a janela no browser só para o rótulo (o servidor recalcula).
 * @param {number} monthsAhead
 * @returns {{ from: string, to: string }}
 */
export function previewWindowLabel(monthsAhead, today = new Date()) {
	const months = Number(monthsAhead) || 3;
	const y = today.getFullYear();
	const m = today.getMonth();
	const start = new Date(y, m + 1, 1);
	const end = new Date(start.getFullYear(), start.getMonth() + months, 0);
	const iso = (d) => {
		const yy = d.getFullYear();
		const mm = String(d.getMonth() + 1).padStart(2, '0');
		const dd = String(d.getDate()).padStart(2, '0');
		return `${yy}-${mm}-${dd}`;
	};
	return { from: iso(start), to: iso(end) };
}

async function loadCompanies(selectEl) {
	const mb = msgbox({ animation: true, text: 'Carregando empresas…' });
	try {
		const res = await api.list_all_companies();
		if (!res.ok) {
			notify(await res.text(), 'Empresas');
			return;
		}
		let list;
		try {
			list = await res.json();
		} catch {
			notify('Resposta de empresas inválida.', 'Empresas');
			return;
		}
		if (!Array.isArray(list)) return;
		selectEl.innerHTML = '<option value="">— Selecione —</option>';
		for (const c of list) {
			const o = document.createElement('option');
			o.value = c.id;
			o.textContent = c.nick || c.name || c.id;
			selectEl.appendChild(o);
		}
	} finally {
		mb.destroy();
	}
}

async function loadPrograms(companyId, selectEl) {
	selectEl.innerHTML = '<option value="">Todos</option>';
	if (!companyId) return;
	try {
		const res = await api.list_programs_by_company(companyId);
		if (!res.ok) return;
		let list;
		try {
			list = await res.json();
		} catch {
			return;
		}
		if (!Array.isArray(list)) return;
		for (const p of list) {
			if (p?.is_disabled) continue;
			const o = document.createElement('option');
			o.value = p.id;
			o.textContent = p.name || `Programa ${p.id}`;
			selectEl.appendChild(o);
		}
	} catch (e) {
		console.error(e);
	}
}

function collectParams(node, format) {
	const companyId = $('#exi-company', node)?.value?.trim();
	if (!companyId) {
		notify('Selecione a empresa.');
		return null;
	}
	let months = parseInt($('#exi-months', node)?.value || '3', 10);
	if (!Number.isFinite(months) || months < 1) months = 3;
	if (months > 12) months = 12;
	const params = {
		format,
		months_ahead: String(months),
	};
	const pid = $('#exi-program', node)?.value?.trim();
	if (pid) params.program_id = pid;
	if (format === 'json') {
		let lim = parseInt($('#exi-limit', node)?.value || '500', 10);
		if (!Number.isFinite(lim) || lim < 1) lim = 500;
		if (lim > 500) lim = 500;
		params.limit = String(lim);
	}
	return { companyId, params };
}

const PREVIEW_COLS = [
	['sku', 'SKU', 'text'],
	['produto', 'Descrição', 'text'],
	['quantidade_disponivel', 'Qtd disponível', 'num'],
	['lote', 'Lote', 'text'],
	['validade', 'Validade', 'text'],
	['fabricante', 'Fabricante', 'text'],
	['fornecedor', 'Fornecedor', 'text'],
	['custo_unitario', 'Custo unit.', 'money'],
	['programa', 'Programa', 'text'],
];

function cellText(row, key, kind) {
	const v = row?.[key];
	if (kind === 'num') return fmtQty(v);
	if (kind === 'money') return v == null || v === '' ? '' : fmtMoney(v, 4);
	return v == null ? '' : String(v);
}

function renderPreview(container, payload) {
	container.replaceChildren();
	const rows = Array.isArray(payload?.rows) ? payload.rows : [];
	const winFrom = fmtIsoBr(payload?.date_from);
	const winTo = fmtIsoBr(payload?.date_to);

	const windowP = document.createElement('p');
	windowP.className = 'mts';
	windowP.append('Janela: ');
	const sFrom = document.createElement('strong');
	sFrom.textContent = winFrom || '—';
	const sTo = document.createElement('strong');
	sTo.textContent = winTo || '—';
	windowP.append(sFrom, ' a ', sTo, ' (sem o mês atual).');
	container.appendChild(windowP);

	if (!rows.length) {
		const p = document.createElement('p');
		p.className = 'muted mts';
		p.textContent = 'Nenhum item disponível para venda nesta janela.';
		container.appendChild(p);
		return;
	}

	const summary = document.createElement('p');
	summary.className = 'mts';
	const s1 = document.createElement('strong');
	s1.textContent = String(rows.length);
	summary.append('Exibindo ', s1, ' linha(s).');
	if (payload?.truncated) {
		summary.append(' Há mais itens — baixe o CSV para a lista completa.');
	}
	container.appendChild(summary);

	const scroll = document.createElement('div');
	scroll.className = 'ibe-scroll mts';
	const table = document.createElement('table');
	table.className = 'ibe-table';
	const thead = document.createElement('thead');
	const hr = document.createElement('tr');
	for (const [, label, kind] of PREVIEW_COLS) {
		const th = document.createElement('th');
		if (kind === 'num' || kind === 'money') th.className = 'ibe-num';
		th.textContent = label;
		hr.appendChild(th);
	}
	thead.appendChild(hr);
	table.appendChild(thead);

	const tbody = document.createElement('tbody');
	const frag = document.createDocumentFragment();
	for (const r of rows) {
		const tr = document.createElement('tr');
		for (const [key, , kind] of PREVIEW_COLS) {
			const td = document.createElement('td');
			if (kind === 'num' || kind === 'money') td.className = 'ibe-num';
			td.textContent = cellText(r, key, kind);
			tr.appendChild(td);
		}
		frag.appendChild(tr);
	}
	tbody.appendChild(frag);
	table.appendChild(tbody);
	scroll.appendChild(table);
	container.appendChild(scroll);
}

function setActionsDisabled(root, disabled) {
	for (const id of ['exi-preview', 'exi-csv']) {
		const btn = $(`#${id}`, root);
		if (btn) btn.disabled = !!disabled;
	}
}

function refreshWindowHint(root) {
	const months = parseInt($('#exi-months', root)?.value || '3', 10);
	const { from, to } = previewWindowLabel(months);
	const el = $('#exi-window-hint', root);
	// <input> exibe value — textContent não atualiza o campo na tela.
	if (el) el.value = `${fmtIsoBr(from)} a ${fmtIsoBr(to)}`;
}

export default async function expiring_items() {
	const work = document.getElementById('workarea');
	work.innerHTML = `
		<style>
			.ibe-hint { color: #555; font-size: 0.9rem; max-width: 52rem; }
			.ibe-scroll {
				overflow: auto;
				max-height: 65vh;
				border: 1px solid var(--color-border, #cbd5e1);
				border-radius: 2px;
				background: #fff;
			}
			.ibe-filters label { display: block; margin-top: 0.5rem; font-size: 0.9rem; }
			.ibe-filters .row { display: flex; flex-wrap: wrap; gap: 0.75rem 1rem; align-items: flex-end; }
			.ibe-filters .row > label { flex: 1 1 11rem; }
			.ibe-actions { margin-top: 1rem; display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; }
			.ibe-filters input, .ibe-filters select { width: 100%; max-width: 18rem; box-sizing: border-box; }
			.ibe-filters .ibe-panel {
				margin-top: 0.75rem;
				padding: 0.75rem 1rem 1rem;
				background: var(--color-secondary, #f8fafc);
				border: 1px solid var(--color-border, #e2e8f0);
			}
			.ibe-num { text-align: right; }
		</style>
		<div class="title">Itens a vencer</div>
		<p class="mts ibe-hint">
			Lotes <strong>disponíveis para venda</strong> cuja validade cai nos
			<strong>meses seguintes</strong> (o mês atual não entra).
			Uma linha por lote. Baixe o <strong>CSV</strong> para abrir no Excel.
			Caminho: <strong>Comercial → Itens a vencer</strong>.
		</p>
		<div class="ibe-filters mts" id="exi-filters">
			<div class="ibe-panel">
				<div class="row">
					<label>Empresa<sup>*</sup>
						<select id="exi-company" required></select>
					</label>
					<label>Meses à frente
						<select id="exi-months">
							<option value="1">1 mês</option>
							<option value="2">2 meses</option>
							<option value="3" selected>3 meses</option>
							<option value="6">6 meses</option>
							<option value="12">12 meses</option>
						</select>
					</label>
					<label>Janela
						<input type="text" id="exi-window-hint" readonly
							title="Calculada sem o mês atual. O servidor confirma na consulta.">
					</label>
					<label>Programa
						<select id="exi-program">
							<option value="">Todos</option>
						</select>
					</label>
					<label>Limite prévia
						<input type="number" id="exi-limit" min="1" max="500" value="500"
							title="Padrão 500. O CSV traz a lista completa.">
					</label>
				</div>
				<div class="ibe-actions">
					<button type="button" class="button" id="exi-preview">Prévia</button>
					<button type="button" class="button" id="exi-csv">CSV</button>
				</div>
			</div>
		</div>
		<div id="exi-preview-host" class="mts"></div>
	`;

	const filters = $('#exi-filters', work);
	const previewHost = $('#exi-preview-host', work);
	const companySel = $('#exi-company', filters);
	const programSel = $('#exi-program', filters);

	refreshWindowHint(filters);
	$('#exi-months', filters)?.addEventListener('change', () => refreshWindowHint(filters));

	await loadCompanies(companySel);

	companySel?.addEventListener('change', async () => {
		const mb = msgbox({ animation: true, text: 'Carregando programas…' });
		try {
			await loadPrograms(companySel.value?.trim(), programSel);
		} finally {
			mb.destroy();
		}
	});

	async function runPreview() {
		const collected = collectParams(filters, 'json');
		if (!collected) return;
		const { companyId, params } = collected;
		setActionsDisabled(filters, true);
		const mb = msgbox({ animation: true, text: 'Gerando prévia…' });
		try {
			const res = await api.expiring_items_report(companyId, params);
			if (!res.ok) {
				notify((await res.text()) || `Erro ${res.status}`, 'Itens a vencer');
				return;
			}
			const data = await res.json();
			if (!data || typeof data !== 'object' || Array.isArray(data) || !Array.isArray(data.rows)) {
				notify('Formato inesperado da API.', 'Itens a vencer');
				return;
			}
			const hint = $('#exi-window-hint', filters);
			if (hint && data.date_from && data.date_to) {
				hint.value = `${fmtIsoBr(data.date_from)} a ${fmtIsoBr(data.date_to)}`;
			}
			renderPreview(previewHost, data);
			notify(`Prévia: ${data.rows.length} linha(s).`, 'Itens a vencer');
		} catch (e) {
			console.error(e);
			notify(String(e?.message || e), 'Itens a vencer');
		} finally {
			mb.destroy();
			setActionsDisabled(filters, false);
		}
	}

	async function runCsv() {
		const collected = collectParams(filters, 'csv');
		if (!collected) return;
		const { companyId, params } = collected;
		setActionsDisabled(filters, true);
		const pg = progress({
			title: 'Gerando CSV…',
			text: 'Consultando lotes a vencer e montando o arquivo.',
			indeterminate: true,
		});
		try {
			pg.markStage('Aguardando resposta do servidor…');
			const res = await api.expiring_items_report(companyId, params);
			if (!res.ok) {
				notify((await res.text()) || `Erro ${res.status}`, 'Itens a vencer');
				return;
			}
			pg.markStage('Processando arquivo…');
			const data = await res.json();
			if (data.csv) {
				downloadBase64(data.csv, data.filename || 'itens-a-vencer.csv', 'text/csv;charset=utf-8');
				notify(`${data.rows || 0} linha(s) exportada(s).`, 'CSV');
				return;
			}
			notify('Resposta inesperada do servidor.', 'Itens a vencer');
		} catch (e) {
			console.error(e);
			notify(String(e?.message || e), 'Itens a vencer');
		} finally {
			pg.destroy();
			setActionsDisabled(filters, false);
		}
	}

	$('#exi-preview', filters)?.addEventListener('click', () => runPreview());
	$('#exi-csv', filters)?.addEventListener('click', () => runCsv());
}
